import {
  Configuration as AppManagementApiConfiguration,
  ConfigurationParameters as AppManagementApiConfigurationParameters,
} from '@era/appmanagement-sdk';
import {
  Configuration as LocationsApiConfiguration,
  ConfigurationParameters as LocationsApiConfigurationParameters,
} from '@era/locations-sdk';
import {
  Configuration as SecurityApiConfiguration,
  ConfigurationParameters as SecurityApiConfigurationParameters,
} from '@era/security-sdk';
import { environment } from '@rocksoft/environment';
import {
  Configuration as MdmDepConfiguration,
  ConfigurationParameters as MdmDepConfigurationParameters,
} from '@rocksoft/mdm-dep-sdk';
import {
  Configuration as MdmApiConfiguration,
  ConfigurationParameters as MdmApiConfigurationParameters,
  ConfigurationParameters as MdmApiConfigurationParametersV2,
  Configuration as MdmApiConfigurationV2,
} from '@rocksoft/mdm-sdk-v2';

import {
  Configuration as DeviceTaskManagementApiConfiguration,
  ConfigurationParameters as DeviceTaskManagementApiConfigurationParameters,
} from '@era/device-task-management-sdk';
import {
  Configuration as GatewayApiConfiguration,
  ConfigurationParameters as GatewayApiConfigurationParameters,
} from '@era/gateway-sdk';
import {
  Configuration as InRoomApiConfiguration,
  ConfigurationParameters as InRoomApiConfigurationParameters,
} from '@era/inroom-sdk';
import {
  Configuration as OsUpdatesApiConfiguration,
  ConfigurationParameters as OsUpdatesApiConfigurationParameters,
} from '@era/osupdates-sdk';
import {
  Configuration as SettingsApiConfiguration,
  ConfigurationParameters as SettingsApiConfigurationParameters,
} from '@era/settings-sdk';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faAndroid, faApple, faWindows } from '@fortawesome/free-brands-svg-icons';
import {
  faBell,
  faCheckCircle,
  faCircle,
  faClock,
  faSave,
  faTrashAlt,
  faUser,
} from '@fortawesome/free-regular-svg-icons';
import {
  faArrowAltCircleUp,
  faArrowCircleUp,
  faBackward,
  faBars,
  faCalendar,
  faCaretDown,
  faCaretRight,
  faCheck,
  faCheckCircle as faCheckCircleSolid,
  faCheckSquare,
  faChevronDown,
  faChevronUp,
  faCircle as faCircleSolid,
  faCog,
  faEllipsisH,
  faExclamation,
  faExclamationTriangle,
  faExternalLinkAlt,
  faEye,
  faEyeSlash,
  faFastBackward,
  faFastForward,
  faFileUpload,
  faFilter,
  faForward,
  faInfoCircle,
  faKey,
  faLightbulb,
  faLocationArrow,
  faLock,
  faLockOpen,
  faMinus,
  faMobileAlt,
  faPause,
  faPauseCircle,
  faPencil,
  faPencilAlt,
  faPlay,
  faPlus,
  faPowerOff,
  faQuestionCircle,
  faRedo,
  faRefresh,
  faSpinner,
  faSync,
  faTabletAlt,
  faTerminal,
  faTimes,
  faTimesCircle,
  faToggleOff,
  faToggleOn,
  faTrash,
  faTriangleExclamation,
  faTv,
  faUpload,
  faVolumeDown,
  faVolumeMute,
  faVolumeUp,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { OidcSecurityService } from 'angular-auth-oidc-client';

// ! TODO: Fix Hacky Access Token
// ! Only works if following happy path, breaks with any actual async delay
export function createAccessTokenResolver(oidcSecurityService: OidcSecurityService): () => string {
  return () => {
    let accessToken: string = '';

    oidcSecurityService.getAccessToken().subscribe((token: string) => (accessToken = token));

    return accessToken;
  };
}

export function locationsApiConfigFactory(authenticationService: OidcSecurityService): LocationsApiConfiguration {
  const params: LocationsApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.locations.api,
  };

  return new LocationsApiConfiguration(params);
}

export function settingsApiConfigFactory(authenticationService: OidcSecurityService): SettingsApiConfiguration {
  const params: SettingsApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.settings.api,
  };

  return new SettingsApiConfiguration(params);
}

export function SecurityApiConfigFactory(authenticationService: OidcSecurityService): SecurityApiConfiguration {
  const params: SecurityApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.groups.api,
  };

  return new SecurityApiConfiguration(params);
}

export function mdmApiConfigFactory(authenticationService: OidcSecurityService): MdmApiConfiguration {
  const params: MdmApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.mdm.api,
  };

  return new MdmApiConfiguration(params);
}

export function mdmApiConfigFactoryV2(authenticationService: OidcSecurityService): MdmApiConfigurationV2 {
  const params: MdmApiConfigurationParametersV2 = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.mdm.api,
  };

  return new MdmApiConfigurationV2(params);
}

export function mdmDepConfigFactory(authenticationService: OidcSecurityService): MdmDepConfiguration {
  const params: MdmDepConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.dep.api,
  };

  return new MdmDepConfiguration(params);
}

export function inRoomApiConfigFactory(authenticationService: OidcSecurityService): InRoomApiConfiguration {
  const params: InRoomApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.inroom.api,
  };

  return new InRoomApiConfiguration(params);
}

export function gatewayApiConfigFactory(authenticationService: OidcSecurityService): GatewayApiConfiguration {
  const params: GatewayApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.gateway.api,
  };

  return new GatewayApiConfiguration(params);
}

export function osUpdatesApiConfigFactory(authenticationService: OidcSecurityService): OsUpdatesApiConfiguration {
  const params: OsUpdatesApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.osupdates.api,
  };

  return new OsUpdatesApiConfiguration(params);
}

export function deviceTaskManagementApiConfigFactory(
  authenticationService: OidcSecurityService
): DeviceTaskManagementApiConfiguration {
  const params: DeviceTaskManagementApiConfigurationParameters = {
    // set configuration parameters here.
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.devicetaskmanagement.api,
  };

  return new DeviceTaskManagementApiConfiguration(params);
}

export function appManagementApiConfigFactory(
  authenticationService: OidcSecurityService
): AppManagementApiConfiguration | undefined {
  if (!environment.features.appmanagement.enabled) {
    return undefined;
  }

  const params: AppManagementApiConfigurationParameters = {
    accessToken: createAccessTokenResolver(authenticationService),
    basePath: environment.appmanagement.api,
  };

  return new AppManagementApiConfiguration(params);
}

export function registerIconsFactory() {
  const library = new FaIconLibrary();

  library.addIcons(
    faBars,
    faCalendar,
    faCheckCircle,
    faCheckCircleSolid,
    faChevronDown,
    faChevronUp,
    faTrash,
    faTrashAlt,
    faCog,
    faPlus,
    faTriangleExclamation,
    faPencilAlt,
    faSave,
    faEye,
    faEyeSlash,
    faTimes,
    faCheck,
    faRedo,
    faPowerOff,
    faKey,
    faSync,
    faFileUpload,
    faCaretRight,
    faCaretDown,
    faLock,
    faBell,
    faLocationArrow,
    faExclamationTriangle,
    faEllipsisH,
    faAndroid,
    faWindows,
    faUpload,
    faTimesCircle,
    faPauseCircle,
    faCircle,
    faCircleSolid,
    faFilter,
    faLockOpen,
    faApple,
    faExclamation,
    faExternalLinkAlt,
    faSpinner,
    faMinus,
    faClock,
    faArrowAltCircleUp,
    faTv,
    faTabletAlt,
    faMobileAlt,
    faWrench,
    faArrowCircleUp,
    faPause,
    faRefresh,
    faTerminal,
    faFastBackward,
    faBackward,
    faPlay,
    faForward,
    faFastForward,
    faVolumeDown,
    faVolumeMute,
    faVolumeUp,
    faLightbulb,
    faToggleOff,
    faToggleOn,
    faUser,
    faQuestionCircle,
    faPencil,
    faCheckSquare,
    faInfoCircle
  );

  return library;
}
